import { ApplicationConfig, provideZoneChangeDetection, importProvidersFrom, LOCALE_ID, APP_INITIALIZER } from '@angular/core';
import { AppStatusService } from './core/services/app-status.service';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors, HttpClient } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { providePrimeNG } from 'primeng/config';
import { MessageService, ConfirmationService } from 'primeng/api';
import Aura from '@primeuix/themes/aura';
import { Observable, of } from 'rxjs';
import { timeout, catchError } from 'rxjs/operators';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { timeoutInterceptor } from './core/interceptors/timeout.interceptor';

// Enregistrer les données de locale français
registerLocaleData(localeFr);

// Custom TranslateLoader pour charger les fichiers de traduction
export class CustomTranslateLoader implements TranslateLoader {
  constructor(private http: HttpClient) { }

  getTranslation(lang: string): Observable<any> {
    // Ajouter un timeout et un fallback pour éviter les blocages
    return this.http.get(`./assets/i18n/${lang}.json`).pipe(
      // Timeout de 5 secondes
      timeout(5000),
      catchError((error) => {
        console.warn(`⚠️ Failed to load translation file for ${lang}, using fallback`, error);
        // Fallback vers français si échec
        if (lang !== 'fr') {
          return this.http.get(`./assets/i18n/fr.json`).pipe(
            timeout(5000),
            catchError(() => {
              console.error('❌ Failed to load fallback translation file');
              return of({});
            })
          );
        }
        return of({});
      })
    );
  }
}

export function HttpLoaderFactory(http: HttpClient): CustomTranslateLoader {
  return new CustomTranslateLoader(http);
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Détection de changement de zone
    provideZoneChangeDetection({ eventCoalescing: true }),

    // Configuration du routeur
    provideRouter(routes),

    // Configuration HTTP avec intercepteurs
    // timeoutInterceptor EN PREMIER : son budget doit englober refresh de jeton et retries.
    provideHttpClient(
      withInterceptors([timeoutInterceptor, authInterceptor, errorInterceptor])
    ),

    // Animations pour PrimeNG
    provideAnimations(),

    // Configuration globale PrimeNG avec thème Aura
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          prefix: 'p',
          darkModeSelector: '.dark-mode',
          cssLayer: false
        }
      }
    }),

    // Services UI globaux
    MessageService,
    ConfirmationService,

    // Chargement de l'état de l'application (mode maintenance) avant rendu
    {
      provide: APP_INITIALIZER,
      useFactory: (appStatus: AppStatusService) => () => appStatus.loadStatus(),
      deps: [AppStatusService],
      multi: true
    },

    // Configuration Locale
    { provide: LOCALE_ID, useValue: 'fr' },

    // Configuration i18n
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'fr',
        fallbackLang: 'fr', // Utiliser fallbackLang au lieu de defaultLanguage
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      })
    )
  ]
};
